import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { 
  ArrowRight, Radio, Activity, BarChart3, 
  Mic, Video, Tv, Camera, Globe, Volume2, VolumeX, Play 
} from 'lucide-react';
import styles from './Hero.module.css';

const Hero = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(true); 
  const [hasInteracted, setHasInteracted] = useState(false);
  const containerRef = useRef(null);
  const videoRef = useRef(null);

  // --- 1. SCROLL-TO-PAUSE LOGIC ---
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (videoRef.current) {
          if (entry.isIntersecting) {
            // Video is visible - play (if user has already clicked once)
            if (hasInteracted) {
                videoRef.current.play().catch(() => {});
            }
          } else {
            // Video is off-screen - pause
            videoRef.current.pause();
          }
        }
      },
      { threshold: 0.2 } // Triggers when only 20% of hero is visible
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, [hasInteracted]); // Re-run when interaction status changes


  // --- 2. VOICE POLICY LOGIC ---
  useEffect(() => {
    const enableVoice = () => {
      setIsMuted(false);
      setHasInteracted(true);
      if (videoRef.current) {
          videoRef.current.muted = false;
          videoRef.current.play().catch(() => {});
      }
      window.removeEventListener('click', enableVoice);
    };
    window.addEventListener('click', enableVoice);
    return () => window.removeEventListener('click', enableVoice);
  }, []);

  // --- 3. MOUSE PARALLAX ---
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothX = useSpring(mouseX, { stiffness: 50, damping: 20 });
  const smoothY = useSpring(mouseY, { stiffness: 50, damping: 20 });

  const rotateX = useTransform(smoothY, [-0.5, 0.5], [10, -10]);
  const rotateY = useTransform(smoothX, [-0.5, 0.5], [-10, 10]);
  const bgX = useTransform(smoothX, [-0.5, 0.5], [50, -50]);
  const bgY = useTransform(smoothY, [-0.5, 0.5], [50, -50]);

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const videos = [
    { url: "https://res.cloudinary.com/ddkyeuhk8/video/upload/v1773915196/a0busojxf6ake5clxfxn.webm", label: "National" },
    { url: "https://res.cloudinary.com/ddkyeuhk8/video/upload/v1773915319/tgmvb9oo0b7rqwxfthsz.webm", label: "Breaking" },
    { url: "https://res.cloudinary.com/ddkyeuhk8/video/upload/v1773918126/ri0cndrudecuoee5v2et.webm", label: "Global" }, 
    { url: "https://res.cloudinary.com/ddkyeuhk8/video/upload/v1773918138/s7zvfknnqqqbrgirhbns.webm", label: "Corporate" },
    { url: "https://res.cloudinary.com/ddkyeuhk8/video/upload/v1773915454/fjqubtwsm4z0vbylumq4.webm", label: "Special" },
  ];

  const handleVideoEnd = () => setActiveIndex((prev) => (prev + 1) % videos.length);

  return (
    <section ref={containerRef} className={styles.heroContainer} onMouseMove={handleMouseMove}>
      
      {/* 1. INTERACTIVE GHOST BACKGROUND */}
      <div className={styles.bgInteractiveLayer}>
        <motion.div style={{ x: bgX, y: bgY }} className={styles.iconCloud}>
          <Mic className={styles.bgIcon} style={{ top: '15%', left: '10%' }} size={120} />
          <Video className={styles.bgIcon} style={{ top: '60%', left: '5%' }} size={150} />
          <Camera className={styles.bgIcon} style={{ top: '10%', right: '15%' }} size={100} />
          <Tv className={styles.bgIcon} style={{ top: '70%', right: '10%' }} size={180} />
          <Globe className={styles.bgIcon} style={{ top: '40%', right: '35%' }} size={80} />
        </motion.div>
        <div className={styles.blobOrange} />
        <div className={styles.blobBlue} />
      </div>

      <div className={styles.contentWrapper}>
        
        {/* 2. TEXT CONTENT */}
        <motion.div className={styles.textContent} initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }}>
          <h1 className={styles.mainTitle}>
            ANANTA <span className={styles.aiText}>AI ANCHORS</span>
          </h1>
          <h2 className={styles.hindiHeadline}>
            आप स्क्रिप्ट दें, <span className={styles.blueAccent}>एंकर हम देंगे।</span>
          </h2>

          <div className={styles.descriptionBox}>
            <p className={styles.heroSubText}>Revolutionize your digital newsroom with next-generation AI anchors.</p>
            <p className={styles.heroHindiDetail}>अब अपना न्यूज़ चैनल शुरू करें बिना स्टूडियो, बिना कैमरा और बिना ह्यूमन एंकर के।</p>
          </div>

          <div className={styles.breadcrumbWrapper}>
            {videos.map((_, idx) => (
              <div key={idx} className={`${styles.crumb} ${idx === activeIndex ? styles.activeCrumb : ''}`} onClick={() => setActiveIndex(idx)}>
                <div className={styles.crumbBar}>
                  {idx === activeIndex && <motion.div layoutId="progress" className={styles.progressFill} />}
                </div>
                <span className={styles.crumbLabel}>0{idx + 1}</span>
              </div>
            ))}
          </div>

          <button className={styles.btnPrimary}>Start Your AI News Channel <ArrowRight size={20}/></button>
        </motion.div>

        {/* 3. SQUARE VIDEO */}
        <div className={styles.visualSide}>
          <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className={styles.squareFrame}>
            <AnimatePresence mode="wait">
              <motion.div key={activeIndex} className={styles.videoWrapper} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.1 }}>
                <video 
                    ref={videoRef} 
                    src={videos[activeIndex].url} 
                    autoPlay 
                    muted={isMuted} 
                    onEnded={handleVideoEnd} 
                    className={styles.customVideo} 
                    playsInline 
                />
                
                <div className={styles.overlayUI}>
                  <div className={styles.topRow}>
                    <div className={styles.liveBadge}><Radio size={14} className={styles.pulseIcon} /> LIVE</div>
                    <div className={styles.audioIcon}>{isMuted ? <VolumeX size={18} /> : <Volume2 size={18} color="#e87355" />}</div>
                  </div>
                  <div className={styles.coordinates}>AI_CORE_v4.2</div>
                </div>

                {!hasInteracted && (
                  <div className={styles.voiceOverlay}>
                    <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 2 }} className={styles.playBtn}>
                      <Play fill="white" size={32} />
                    </motion.div>
                    <span>Click anywhere for voice</span>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>

          {/* Widgets */}
          <motion.div className={styles.statWidget} style={{ x: useTransform(smoothX, [-0.5, 0.5], [60, -60]), y: useTransform(smoothY, [-0.5, 0.5], [-30, 30]) }}>
            <Activity size={18} color="#5ba5e0" /> <span>RENDER: 120FPS</span>
          </motion.div>
          <motion.div className={styles.signalWidget} style={{ x: useTransform(smoothX, [-0.5, 0.5], [-50, 50]), y: useTransform(smoothY, [-0.5, 0.5], [60, -60]) }}>
            <BarChart3 size={18} color="#e87355" /> <span>LATENCY: 12ms</span>
          </motion.div>
        </div>
      </div>

      <div className={styles.tickerContainer}>
        <div className={styles.tickerTitle}>ANANTA UPDATE</div>
        <div className={styles.tickerTrack}>
          <div className={styles.tickerContent}>
            <span> • NO STUDIO REQUIRED • 100+ LANGUAGES SUPPORTED • 4K AI ANCHORS READY • START YOUR CHANNEL TODAY • NO CAMERA NEEDED • </span>
            <span> • NO STUDIO REQUIRED • 100+ LANGUAGES SUPPORTED • 4K AI ANCHORS READY • START YOUR CHANNEL TODAY • NO CAMERA NEEDED • </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;